import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { cacheCreds } from '../lib/hermesChats';

// GET /api/agent/hermes/session/ now returns:
//   { baseUrl, sessionToken, expiresAt, sessionKey, wakeMs, chart }
// where baseUrl is the Cloudflare Worker (hermes-proxy) — NOT Daytona.
// sessionToken is an HMAC-signed payload that the worker unpacks to find
// the user's actual sandbox URL + Hermes API key. The browser never sees
// either. `chart` = { baseUrl, sessionToken, expiresAt } is the SAME worker
// but a token targeting the sandbox's brochart WS bridge (port 7891) — Stage 3.
// Both channels target one sandbox, so they share this single broker call /
// wake / refresh (option A). Backend: agent_harness/views.py:HermesSessionView
export default function useHermesSession() {
  const [state, setState] = useState({
    baseUrl: null,
    sessionToken: null,
    sessionKey: null,
    wakeMs: null,
    expiresAt: 0,
    chart: null,
    filesBase: null,
    loading: true,
    error: null,
  });
  const refreshTimer = useRef(null);

  const fetchSession = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/agent/hermes/session/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      // Cache creds so the global sidebar can call the Hermes session API
      // (list/delete/rename) without its own broker/wake — see lib/hermesChats.js.
      if (data.baseUrl && data.sessionToken) cacheCreds(data.baseUrl, data.sessionToken, data.expiresAt);
      setState({
        baseUrl: data.baseUrl,
        sessionToken: data.sessionToken,
        sessionKey: data.sessionKey,
        wakeMs: data.wakeMs || null,
        expiresAt: data.expiresAt || 0,
        chart: data.chart || null,
        filesBase: data.filesBase || null,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err.message || 'broker failure' }));
    }
  }, []);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // Keep the token fresh. A single setTimeout is fragile: browsers heavily
  // throttle timers in backgrounded tabs, so it can fire long after expiry (the
  // chart WS then 401s). Instead poll every 60s AND re-check on tab focus /
  // visibility — refetching once we're within 5 min of expiry. fetchSession sets
  // loading, which guards against overlapping calls; a fresh token advances
  // expiresAt so the window self-closes.
  useEffect(() => {
    if (!state.expiresAt) return;
    const check = () => {
      if (state.loading) return;
      const msLeft = state.expiresAt * 1000 - Date.now();
      if (msLeft < 5 * 60 * 1000) fetchSession();
    };
    refreshTimer.current = setInterval(check, 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(refreshTimer.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [state.expiresAt, state.loading, fetchSession]);

  return { ...state, refresh: fetchSession };
}
